"use client";

import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Button } from "@astryxdesign/core/Button";
import { Icon } from "@astryxdesign/core/Icon";
import { registerOverlayPortal } from "@puckeditor/core";
import { LandingStyledTextField } from "@/components/admin/landings/landing-styled-text-field";
import { researchIcons } from "@/lib/icons/research-icons";
import {
  escapeTextToHtml,
} from "@/lib/landing/puck/landing-rich-html";
import {
  landingAlignWrap,
  resolveStyledText,
  styledTextString,
  type LandingTextValue,
} from "@/lib/landing/puck/styled-text";
import {
  comparisonCellContent,
  type LandingComparisonCell,
  type LandingComparisonCellMode,
} from "@/lib/landing/puck/comparison-defaults";

const THEME_CSS_VARS = [
  "--color-background-body",
  "--color-background-muted",
  "--color-background-surface",
  "--color-background-popover",
  "--color-surface-container",
  "--color-border",
  "--color-text-primary",
  "--color-text-secondary",
  "--color-accent",
  "--landing-accent",
] as const;

let activeEditorId: string | null = null;
let activeEditorClose: (() => void) | null = null;

function claimCanvasEditor(id: string, close: () => void) {
  if (activeEditorId && activeEditorId !== id) {
    activeEditorClose?.();
  }
  activeEditorId = id;
  activeEditorClose = close;
}

function releaseCanvasEditor(id: string) {
  if (activeEditorId !== id) return;
  activeEditorId = null;
  activeEditorClose = null;
}

function stopKeys(event: { stopPropagation: () => void }) {
  event.stopPropagation();
}

function stopPuck(event: { stopPropagation: () => void }) {
  event.stopPropagation();
}

function themeRootOf(node: Element | null): HTMLElement | null {
  if (!node) return null;
  return (
    (node.closest(".landing-cms-theme") as HTMLElement | null) ??
    (node.closest("[data-landing-resolved]") as HTMLElement | null)
  );
}

function previewIsDark(from: Element): boolean {
  const root = themeRootOf(from) ?? from.ownerDocument.documentElement;
  const resolved = root.getAttribute("data-landing-resolved");
  if (resolved === "dark") return true;
  if (resolved === "light") return false;
  const theme = root.getAttribute("data-theme");
  if (theme === "dark") return true;
  if (theme === "light") return false;
  return from.ownerDocument.documentElement.getAttribute("data-theme") === "dark";
}

function documentsForOutsideClick(anchor: HTMLElement): Document[] {
  const docs = new Set<Document>([anchor.ownerDocument]);
  try {
    docs.add(window.document);
    window.document.querySelectorAll("iframe").forEach((iframe) => {
      const doc = iframe.contentDocument;
      if (doc) docs.add(doc);
    });
  } catch {
    // iframe без доступа — слушаем только документ якоря
  }
  return [...docs];
}

function copyThemeVars(from: Element, to: HTMLElement) {
  const source = themeRootOf(from) ?? from;
  const styles = source.ownerDocument.defaultView?.getComputedStyle(source);
  if (!styles) return;
  for (const key of THEME_CSS_VARS) {
    const value = styles.getPropertyValue(key).trim();
    if (value) to.style.setProperty(key, value);
  }
}

function popupStyle(
  anchor: DOMRect,
  width: number,
  height: number,
  view: Window,
): { top: number; left: number; width: number } {
  let left = anchor.left + anchor.width / 2 - width / 2;
  left = Math.max(8, Math.min(left, view.innerWidth - width - 8));
  const below = anchor.bottom + 8;
  const top =
    below + height > view.innerHeight - 8
      ? Math.max(8, anchor.top - height - 8)
      : below;
  return { top, left, width };
}

function toCanvasEditorHtml(value: LandingTextValue): string {
  const parsed = resolveStyledText(value);
  if (parsed.html) return parsed.html;
  return escapeTextToHtml(parsed.text);
}

function measurePreviewStyle(previewRoot: HTMLElement | null): CSSProperties | undefined {
  const preview =
    previewRoot?.firstElementChild instanceof HTMLElement
      ? previewRoot.firstElementChild
      : previewRoot;
  if (!preview) return undefined;
  const cs = preview.ownerDocument.defaultView?.getComputedStyle(preview);
  if (!cs) return undefined;
  return {
    fontSize: cs.fontSize,
    fontWeight: cs.fontWeight as CSSProperties["fontWeight"],
    fontFamily: cs.fontFamily,
    fontStyle: cs.fontStyle,
    lineHeight: cs.lineHeight,
    letterSpacing: cs.letterSpacing,
    color: cs.color,
    textTransform: cs.textTransform as CSSProperties["textTransform"],
  };
}

const MODES: {
  mode: LandingComparisonCellMode;
  label: string;
  icon: typeof researchIcons.check;
}[] = [
  { mode: "check", label: "Галочка", icon: researchIcons.check },
  { mode: "cross", label: "Крест", icon: researchIcons.close },
  { mode: "text", label: "Текст", icon: researchIcons.pencil },
];

function FloatingPanel({
  anchorEl,
  children,
  onClose,
}: {
  anchorEl: HTMLElement;
  children: ReactNode;
  onClose: () => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const view = anchorEl.ownerDocument.defaultView ?? window;
  const isDark = previewIsDark(anchorEl);
  const [pos, setPos] = useState(() =>
    popupStyle(anchorEl.getBoundingClientRect(), 360, 320, view),
  );

  useLayoutEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    copyThemeVars(anchorEl, el);
    function place() {
      const panel = panelRef.current;
      if (!panel) return;
      setPos(
        popupStyle(
          anchorEl.getBoundingClientRect(),
          360,
          panel.offsetHeight,
          view,
        ),
      );
    }
    place();
    const observer = new ResizeObserver(place);
    observer.observe(el);
    return () => observer.disconnect();
  }, [anchorEl, view]);

  useEffect(() => {
    let remove: (() => void) | undefined;
    const docs = documentsForOutsideClick(anchorEl);
    const timer = view.setTimeout(() => {
      function onPointer(event: PointerEvent) {
        const target = event.target;
        if (!(target instanceof Node)) return;
        if (panelRef.current?.contains(target)) return;
        if (
          target instanceof Element &&
          (target.closest("[data-canvas-editor-trigger]") ||
            target.closest("[data-canvas-inline-editor]"))
        ) {
          return;
        }
        onCloseRef.current();
      }
      function onKey(event: KeyboardEvent) {
        if (event.key !== "Escape") return;
        event.stopPropagation();
        onCloseRef.current();
      }
      for (const doc of docs) {
        doc.addEventListener("pointerdown", onPointer, true);
        doc.addEventListener("keydown", onKey, true);
      }
      remove = () => {
        for (const doc of docs) {
          doc.removeEventListener("pointerdown", onPointer, true);
          doc.removeEventListener("keydown", onKey, true);
        }
      };
    }, 0);
    return () => {
      view.clearTimeout(timer);
      remove?.();
    };
  }, [anchorEl, view]);

  const host = anchorEl.ownerDocument.body;

  return createPortal(
    <div
      ref={(el) => {
        panelRef.current = el;
        if (el) copyThemeVars(anchorEl, el);
      }}
      role="dialog"
      data-comparison-editor=""
      data-theme={isDark ? "dark" : "light"}
      className="fixed z-[300] rounded-2xl border border-border bg-[var(--color-background-popover)] p-3 text-[var(--color-text-primary)] shadow-xl"
      style={{ top: pos.top, left: pos.left, width: pos.width }}
      onPointerDown={stopPuck}
      onMouseDown={stopPuck}
      onKeyDown={stopKeys}
      onKeyUp={stopKeys}
    >
      {children}
    </div>,
    host,
  );
}

function EditorChrome({
  label,
  children,
  onDone,
}: {
  label?: string;
  children: ReactNode;
  onDone: () => void;
}) {
  return (
    <div className="flex w-full min-w-0 flex-col gap-3">
      {label ? <p className="text-xs text-[var(--color-text-secondary)]">{label}</p> : null}
      {children}
      <div className="flex justify-end border-t border-border pt-2">
        <Button label="Готово" size="sm" variant="primary" onClick={onDone} />
      </div>
    </div>
  );
}

function useExclusiveEditor() {
  const editorId = useId();
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const flushRef = useRef<() => void>(() => {});
  const closeRef = useRef(() => {});

  closeRef.current = () => {
    flushRef.current();
    setOpen(false);
    setAnchorEl(null);
  };

  useEffect(() => {
    if (!open) {
      releaseCanvasEditor(editorId);
      return;
    }
    claimCanvasEditor(editorId, () => closeRef.current());
    return () => releaseCanvasEditor(editorId);
  }, [editorId, open]);

  function openAt(el: HTMLElement) {
    claimCanvasEditor(editorId, () => closeRef.current());
    setAnchorEl(el);
    setOpen(true);
  }

  function closeAndSave() {
    closeRef.current();
  }

  return { open, anchorEl, flushRef, openAt, closeAndSave };
}

function InlineSurface({
  label,
  multiline,
  inline,
  alignStyle,
  editStyle,
  previewTheme,
  surfaceRef,
}: {
  label: string;
  multiline?: boolean;
  inline?: boolean;
  alignStyle?: CSSProperties;
  editStyle?: CSSProperties;
  /** Какую тему показываем на канвасе (переключатель в попапе). */
  previewTheme: "light" | "dark";
  surfaceRef: (el: HTMLDivElement | null) => void;
}) {
  const typography = editStyle
    ? (({ color: _c, ...rest }) => rest)(editStyle)
    : undefined;
  return (
    <div
      ref={surfaceRef}
      role="textbox"
      aria-label={label}
      aria-multiline={multiline ? "true" : "false"}
      contentEditable
      suppressContentEditableWarning
      data-landing-rich
      data-landing-resolved={previewTheme}
      data-canvas-inline-editor=""
      className={[
        "landing-rich-preview min-w-0 outline-none break-words [overflow-wrap:anywhere]",
        inline ? "inline-block" : "block w-full",
        multiline ? "min-h-[1.5em] whitespace-pre-wrap" : "",
        previewTheme === "dark"
          ? "rounded-md bg-[#1c1c1c] px-1 py-0.5 text-[#f3f3f3]"
          : "",
      ].join(" ")}
      style={{ ...typography, ...alignStyle }}
      onPointerDown={stopPuck}
      onMouseDown={stopPuck}
      onKeyDown={(event) => {
        event.stopPropagation();
        if (event.key !== "Enter") return;
        if (!multiline) {
          event.preventDefault();
          return;
        }
        event.preventDefault();
        const doc = event.currentTarget.ownerDocument;
        if (!doc.execCommand("insertLineBreak")) {
          const selection = doc.defaultView?.getSelection();
          if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            range.deleteContents();
            const br = doc.createElement("br");
            range.insertNode(br);
            range.setStartAfter(br);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }
      }}
    />
  );
}

/** Текст правится на канвасе; попап — только инструменты. */
export function CanvasClickTextEditor({
  value,
  onChange,
  label,
  multiline,
  children,
  inline,
  alignFallback,
}: {
  value: LandingTextValue;
  onChange: (value: LandingTextValue) => void;
  label: string;
  multiline?: boolean;
  children: ReactNode;
  /** Бейдж / кнопка — без растягивания на всю ширину. */
  inline?: boolean;
  alignFallback?: "left" | "center" | "right";
}) {
  const { open, anchorEl, flushRef, openAt, closeAndSave } = useExclusiveEditor();
  const previewDark = anchorEl ? previewIsDark(anchorEl) : false;
  const align = inline ? null : landingAlignWrap(value, alignFallback);
  const portalCleanup = useRef<(() => void) | undefined>(undefined);
  const surfaceElRef = useRef<HTMLDivElement | null>(null);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const [hostEditor, setHostEditor] = useState<HTMLElement | null>(null);
  const [editStyle, setEditStyle] = useState<CSSProperties | undefined>();
  const [previewTheme, setPreviewTheme] = useState<"light" | "dark">("light");
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    return () => portalCleanup.current?.();
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setHostEditor(null);
      setEditStyle(undefined);
      return;
    }
    const surface = surfaceElRef.current;
    if (!surface) return;

    setEditStyle(measurePreviewStyle(previewRef.current));
    surface.innerHTML = toCanvasEditorHtml(valueRef.current) || "<br>";
    setHostEditor(surface);
    surface.focus();
    const selection = surface.ownerDocument.defaultView?.getSelection();
    if (selection) {
      const range = surface.ownerDocument.createRange();
      range.selectNodeContents(surface);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }, [open]);

  function beginEdit(el: HTMLElement) {
    setPreviewTheme(previewIsDark(el) ? "dark" : "light");
    setEditStyle(measurePreviewStyle(previewRef.current));
    openAt(el);
  }

  return (
    <>
      <div
        title={open ? undefined : "Нажмите, чтобы править"}
        data-canvas-editor-trigger=""
        className={[
          inline ? "inline-flex max-w-full shrink-0" : "block w-full",
          "min-w-0 rounded-md outline-none",
          open
            ? "cursor-text ring-1 ring-border"
            : "cursor-text hover:ring-1 hover:ring-border focus-visible:ring-1 focus-visible:ring-border",
          align?.className ?? "",
        ].join(" ")}
        style={align?.style}
        ref={(el) => {
          portalCleanup.current?.();
          portalCleanup.current = undefined;
          if (!el) return;
          portalCleanup.current = registerOverlayPortal(el, {
            disableDrag: true,
          });
        }}
        onPointerDown={stopPuck}
        onMouseDown={stopPuck}
        onClick={(event) => {
          event.stopPropagation();
          if (open) return;
          event.preventDefault();
          beginEdit(event.currentTarget);
        }}
        onKeyDown={(event) => {
          if (open) {
            event.stopPropagation();
            return;
          }
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          event.stopPropagation();
          beginEdit(event.currentTarget);
        }}
      >
        {/* Превью держим смонтированным скрытым в режиме правки — для замера стилей уже сделан; после open достаточно editStyle. */}
        <div
          ref={previewRef}
          className={open ? "hidden" : "pointer-events-none min-w-0"}
          aria-hidden={open || undefined}
        >
          {children}
        </div>
        {open ? (
          <InlineSurface
            label={label}
            multiline={multiline}
            inline={inline}
            alignStyle={
              align?.style?.textAlign
                ? { textAlign: align.style.textAlign }
                : undefined
            }
            editStyle={editStyle}
            previewTheme={previewTheme}
            surfaceRef={(el) => {
              surfaceElRef.current = el;
            }}
          />
        ) : null}
      </div>
      {open && anchorEl ? (
        <FloatingPanel anchorEl={anchorEl} onClose={closeAndSave}>
          <EditorChrome label={label} onDone={closeAndSave}>
            <LandingStyledTextField
              value={value}
              onChange={onChange}
              label={label}
              multiline={multiline}
              toolbarOnly
              hostEditor={hostEditor}
              previewDark={previewDark}
              onEditThemeChange={setPreviewTheme}
              registerFlush={(flush) => {
                flushRef.current = flush;
              }}
            />
          </EditorChrome>
        </FloatingPanel>
      ) : null}
    </>
  );
}

export function ComparisonValueCellEditor({
  cell,
  display,
  onCommit,
}: {
  cell: LandingComparisonCell;
  emphasized?: boolean;
  display: ReactNode;
  onCommit: (next: LandingComparisonCell) => void;
}) {
  const { open, anchorEl, flushRef, openAt, closeAndSave } = useExclusiveEditor();
  const previewDark = anchorEl ? previewIsDark(anchorEl) : false;
  const align =
    cell.mode === "text"
      ? landingAlignWrap(comparisonCellContent(cell), "center")
      : null;
  const surfaceElRef = useRef<HTMLDivElement | null>(null);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const [hostEditor, setHostEditor] = useState<HTMLElement | null>(null);
  const [editStyle, setEditStyle] = useState<CSSProperties | undefined>();
  const [previewTheme, setPreviewTheme] = useState<"light" | "dark">("light");
  const textValue = comparisonCellContent(cell);
  const textValueRef = useRef(textValue);
  textValueRef.current = textValue;

  useLayoutEffect(() => {
    if (!open || cell.mode !== "text") {
      setHostEditor(null);
      return;
    }
    const surface = surfaceElRef.current;
    if (!surface) return;
    setEditStyle(measurePreviewStyle(previewRef.current));
    surface.innerHTML = toCanvasEditorHtml(textValueRef.current) || "<br>";
    setHostEditor(surface);
    surface.focus();
  }, [open, cell.mode]);

  function setMode(mode: LandingComparisonCellMode) {
    if (cell.mode === "text" && mode !== "text") {
      flushRef.current();
    }
    if (mode === "text") {
      onCommit({
        mode: "text",
        text: cell.mode === "text" ? cell.text : "",
        content: cell.mode === "text" ? comparisonCellContent(cell) : "",
      });
      return;
    }
    onCommit({ mode, text: "" });
  }

  function beginEdit(el: HTMLElement) {
    setPreviewTheme(previewIsDark(el) ? "dark" : "light");
    setEditStyle(measurePreviewStyle(previewRef.current));
    openAt(el);
  }

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        title="Нажмите, чтобы править"
        data-canvas-editor-trigger=""
        className={
          align
            ? `min-h-10 w-full min-w-0 cursor-pointer rounded-md px-1 py-1 ${
                open ? "ring-1 ring-border" : "hover:ring-1 hover:ring-border"
              } ${align.className}`
            : `flex min-h-10 w-full min-w-0 cursor-pointer items-center justify-center rounded-md px-1 py-1 ${
                open ? "ring-1 ring-border" : "hover:ring-1 hover:ring-border"
              }`
        }
        style={align?.style}
        onPointerDown={stopPuck}
        onMouseDown={stopPuck}
        onClick={(event) => {
          event.stopPropagation();
          if (open) return;
          beginEdit(event.currentTarget);
        }}
        onKeyDown={(event) => {
          if (open) {
            event.stopPropagation();
            return;
          }
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          event.stopPropagation();
          beginEdit(event.currentTarget);
        }}
      >
        <div
          ref={previewRef}
          className={open && cell.mode === "text" ? "hidden" : undefined}
          aria-hidden={open && cell.mode === "text" ? true : undefined}
        >
          {!(open && cell.mode === "text") ? display : null}
        </div>
        {open && cell.mode === "text" ? (
          <InlineSurface
            label="Текст ячейки"
            multiline
            alignStyle={
              align?.style?.textAlign
                ? { textAlign: align.style.textAlign }
                : { textAlign: "center" }
            }
            editStyle={editStyle}
            previewTheme={previewTheme}
            surfaceRef={(el) => {
              surfaceElRef.current = el;
            }}
          />
        ) : null}
      </div>
      {open && anchorEl ? (
        <FloatingPanel anchorEl={anchorEl} onClose={closeAndSave}>
          <EditorChrome onDone={closeAndSave}>
            <div
              role="radiogroup"
              aria-label="Тип ячейки"
              className="flex flex-wrap items-center justify-center gap-1"
            >
              {MODES.map((item) => {
                const selected = cell.mode === item.mode;
                return (
                  <button
                    key={item.mode}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    title={item.label}
                    className={`inline-flex h-8 items-center gap-1 rounded-full border-0 px-2.5 text-xs ${
                      selected
                        ? "bg-[var(--color-background-muted)] font-medium text-[var(--color-text-primary)]"
                        : "bg-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
                    }`}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => setMode(item.mode)}
                  >
                    <Icon icon={item.icon} size="sm" />
                    {item.label}
                  </button>
                );
              })}
            </div>
            {cell.mode === "text" ? (
              <LandingStyledTextField
                value={textValue}
                onChange={(content) =>
                  onCommit({
                    mode: "text",
                    text: styledTextString(content),
                    content,
                  })
                }
                label="Текст ячейки"
                multiline
                toolbarOnly
                hostEditor={hostEditor}
                previewDark={previewDark}
                onEditThemeChange={setPreviewTheme}
                registerFlush={(flush) => {
                  flushRef.current = flush;
                }}
              />
            ) : (
              <div className="flex justify-center py-1">{display}</div>
            )}
          </EditorChrome>
        </FloatingPanel>
      ) : null}
    </>
  );
}
